import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import { useTwoFactorChallenge } from '@presentation/hooks/useAuth';
import { paths } from '@app/routes/paths';

interface LocationState {
  challengeToken?: string;
}

interface ChallengeFormValues {
  code: string;
}

export const TwoFactorChallengePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const challenge = useTwoFactorChallenge();

  const challengeToken = (location.state as LocationState | null)?.challengeToken;

  if (!challengeToken) {
    return <Navigate to={paths.login} replace />;
  }

  const handleFinish = (values: ChallengeFormValues) => {
    challenge.mutate(
      { challengeToken, code: values.code },
      { onSuccess: () => navigate(paths.dashboard, { replace: true }) },
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <Typography.Title level={4} className="!mb-1">
          Two-factor verification
        </Typography.Title>
        <Typography.Text type="secondary">
          Enter the 6-digit code from your authenticator app, or one of your recovery codes.
        </Typography.Text>

        <Form layout="vertical" className="mt-6" onFinish={handleFinish}>
          <Form.Item
            name="code"
            label="Code"
            rules={[{ required: true, message: 'Code is required' }]}
          >
            <Input
              prefix={<SafetyOutlined />}
              autoComplete="one-time-code"
              size="large"
              autoFocus
            />
          </Form.Item>

          {challenge.isError && (
            <Alert type="error" showIcon title={challenge.error.message} className="mb-4" />
          )}

          <Button type="primary" htmlType="submit" size="large" block loading={challenge.isPending}>
            Verify
          </Button>
        </Form>
      </Card>
    </div>
  );
};
